import { Color, Color as DesignTokensColor } from '@persona-id/design-tokens';
import { isEqual } from 'lodash';

import { NodePosition } from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import { G6DirectedEdgeModelConfig } from 'dashboard/components/GraphCanvasV2/nodes-g6/register-data-nodes';
import {
  CanvasSelectable,
  CollapsedGroup,
  CollapsedGroupId,
  collapsedGroupIdToSelectable,
  CollapsedPairwiseNode,
  CollapsedPairwiseNodeId,
  collapsedPairwiseNodeIdToSelectable,
  DatumTypeToken,
  Edge,
  G6CollapsedGroup,
  G6CollapsedPairwiseNode,
  G6Edge,
  G6EdgeId,
  G6GraphUpdateMessage,
  G6Node,
  G6NodeId,
  HashableNodeId,
  Node,
  NodeId,
  nodeIdFromHashable,
  nodeIdToHashable,
  nodeIdToSelectable,
  NodeState,
  RenderedCanvasNodeType,
} from 'dashboard/models/graph';
import {
  AccountNodeColorlookupFn,
  NodeRenderConfigLookupFn,
} from 'dashboard/models/graph-explorer';
import {
  LightlyHydratedNode,
  NodeIdAndLightHydration,
  ServerLightlyHydratedTransactions,
  TransactionAccountInfo,
} from 'dashboard/models/graph-hydration';
import { ACCOUNT_DATUM_TYPE, G6_DIRECTED_EDGE } from 'lib/constants/graph';

export type G6Type = string & { readonly G6Type: unique symbol };

export const PROPERTY_NODE_TYPE_G6_TYPE = 'graph_property_node';

export const COLLAPSED_GROUP_TYPE_G6_TYPE = 'graph_collapsed_group';

export const COLLAPSED_PAIRWISE_NODE_TYPE_G6_TYPE = 'graph_collapsed_pairwise_node';

export const datumTypeTokenToG6Type = (datumTypeToken: DatumTypeToken): G6Type => {
  if (datumTypeToken === ACCOUNT_DATUM_TYPE.token) {
    return `Graph_${datumTypeToken}` as G6Type;
  } else {
    return PROPERTY_NODE_TYPE_G6_TYPE as G6Type;
  }
};

export const nodeIdToG6Id = (nodeId: NodeId): G6NodeId => {
  return `${RenderedCanvasNodeType.Node}|${nodeId.token}:${nodeId.value}`;
};

export const collapsedGroupIdToG6Id = (collapsedGroup: CollapsedGroupId): G6NodeId => {
  return `${RenderedCanvasNodeType.CollapsedGroup}|${collapsedGroup}`;
};

export const collapsedPairwiseNodeIdToG6Id = (
  collapsedPairwiseNodeId: CollapsedPairwiseNodeId,
): G6NodeId => {
  return `${RenderedCanvasNodeType.CollapsedPairwiseNode}|${collapsedPairwiseNodeId}`;
};

export const canvasSelectableToG6Id = (s: CanvasSelectable) => {
  switch (s.type) {
    case RenderedCanvasNodeType.Node:
      return nodeIdToG6Id(s.selectable);
    case RenderedCanvasNodeType.CollapsedGroup:
      return collapsedGroupIdToG6Id(s.selectable);
    case RenderedCanvasNodeType.CollapsedPairwiseNode:
      return collapsedPairwiseNodeIdToG6Id(s.selectable);
  }
};

export const canvasSelectableFromG6Id = (g6Id: G6NodeId) => {
  const maybeNodeId = g6IdToNodeId(g6Id);
  if (maybeNodeId) {
    return nodeIdToSelectable(maybeNodeId);
  }
  const maybeCollapsedGroupId = g6IdToCollapsedGroupId(g6Id);
  if (maybeCollapsedGroupId) {
    return collapsedGroupIdToSelectable(maybeCollapsedGroupId);
  }
  const maybeCollapsedPairwiseNodeId = g6IdToCollapsedPairwiseNodeId(g6Id);
  if (maybeCollapsedPairwiseNodeId) {
    return collapsedPairwiseNodeIdToSelectable(maybeCollapsedPairwiseNodeId);
  }
  throw new Error(`Cannot parse g6Id ${g6Id} as Selectable`);
};

export const g6IdToNodeId = (g6Id: G6NodeId): NodeId | null => {
  // @ts-expect-error This regex uses es2018 syntax
  const r = g6Id.match(/(?<canvasNodeType>[^]*)[|](?<token>[^:]*)[:](?<value>.*)/);
  if (r?.groups?.canvasNodeType !== RenderedCanvasNodeType.Node) {
    return null;
  }
  const { token, value } = r.groups;
  return {
    token,
    value,
  };
};

export const g6IdToCollapsedGroupId = (g6Id: G6NodeId): CollapsedGroupId => {
  // @ts-expect-error This regex uses es2018 syntax
  const r = g6Id.match(/(?<canvasNodeType>[^]*)[|](?<id>[^]*)/);
  if (r?.groups?.canvasNodeType !== RenderedCanvasNodeType.CollapsedGroup) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  }
  const { id } = r.groups;
  return id;
};

export const g6IdToCollapsedPairwiseNodeId = (g6Id: G6NodeId): CollapsedPairwiseNodeId => {
  // @ts-expect-error This regex uses es2018 syntax
  const r = g6Id.match(/(?<canvasNodeType>[^]*)[|](?<id>[^]*)/);
  if (r?.groups?.canvasNodeType !== RenderedCanvasNodeType.CollapsedPairwiseNode) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  }
  const { id } = r.groups;
  return id;
};

export const g6IdToHashableNodeId = (g6Id: G6NodeId): HashableNodeId => {
  const nodeId = g6IdToNodeId(g6Id);
  if (nodeId) {
    return nodeIdToHashable(nodeId);
  }
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return null;
};

export const hashableNodeIdToG6Id = (hashableNodeId: HashableNodeId) => {
  return nodeIdToG6Id(nodeIdFromHashable(hashableNodeId));
};

const DIMMED_COLOUR = '#EDEDF0';

export const collapsedPairwiseNodeToG6Node = (
  collapsedPairwiseNode: CollapsedPairwiseNode,
): G6Node => {
  return {
    id: collapsedPairwiseNodeIdToG6Id(collapsedPairwiseNode.id.value),
    type: COLLAPSED_PAIRWISE_NODE_TYPE_G6_TYPE,
    visible: collapsedPairwiseNode.state.visible,
    style: {},
    dataNode: collapsedPairwiseNode,
  };
};

export const collapsedPairwiseNodeToG6CollapsedPairwiseNode = (
  collapsedPairwiseNode: CollapsedPairwiseNode,
): G6CollapsedPairwiseNode => {
  return {
    id: collapsedPairwiseNodeIdToG6Id(collapsedPairwiseNode.id.value),
    type: COLLAPSED_PAIRWISE_NODE_TYPE_G6_TYPE,
    visible: collapsedPairwiseNode.state.visible,
    style: {},
    dataNode: collapsedPairwiseNode,
  };
};

export const nodeToG6Node = (
  node: Node,
  lightlyHydratedNode: LightlyHydratedNode,
  nodeRenderConfigLookupFn: NodeRenderConfigLookupFn,
  accountNodeColorLookupFn: AccountNodeColorlookupFn,
): G6Node => {
  // HAX: Used for the demo to make banned node render differently.
  let fillOverride: string;
  if (node.state.dimmed) {
    // custom dimmed node color "Slate 75"
    fillOverride = DIMMED_COLOUR;
  } else if (
    node.id.token === ACCOUNT_DATUM_TYPE.token &&
    // we shouldn't have to check for this but right now not every canvas knows
    // how to hydrate nodes.
    // TODO: Lewis remove this check for lightlyHydratedNode
    lightlyHydratedNode &&
    lightlyHydratedNode.nodeType === 'account'
  ) {
    fillOverride = accountNodeColorLookupFn(lightlyHydratedNode.hydrationData, node.state.featured);
  } else {
    fillOverride = nodeRenderConfigLookupFn(node.id.token).color.hex();
  }

  return {
    id: nodeIdToG6Id(node.id),
    type: datumTypeTokenToG6Type(node.id.token),
    visible: node.state.visible,
    style: {
      fill: fillOverride,
    },

    // Extra information that we pass around to G6.
    dataNode: node,
  };
};

export const collapsedGroupToG6CollapsedGroup = (
  collapsedGroup: CollapsedGroup,
): G6CollapsedGroup => {
  return {
    id: collapsedGroupIdToG6Id(collapsedGroup.groupId),
    type: COLLAPSED_GROUP_TYPE_G6_TYPE,
    visible: collapsedGroup.state.visible,
    style: {},

    // Extra information that we pass around to G6.
    dataNode: collapsedGroup,
  };
};

export const setG6NodePosition = (node: G6Node, position: NodePosition) => {
  node.x = position.x;
  node.y = position.y;
};

export const g6EdgeId = (sourceId: G6NodeId, targetId: G6NodeId): G6EdgeId => {
  if (sourceId < targetId) {
    return `${sourceId}:${targetId}`;
  }
  return `${targetId}:${sourceId}`;
};

export const styleG6Edge = (
  g6Edge: G6Edge,
  sourceNodeState: NodeState,
  targetNodeState: NodeState,
) => {
  const isVisible = sourceNodeState.visible && targetNodeState.visible;
  // make the edge lighter if any connected nodes are dimmed
  let stroke =
    sourceNodeState.dimmed ||
    targetNodeState.dimmed ||
    sourceNodeState.excluded ||
    targetNodeState.excluded
      ? DIMMED_COLOUR
      : Color.CoreSlate200;
  if (sourceNodeState.new || targetNodeState.new) {
    stroke = DesignTokensColor.CoreGreen600;
  }

  g6Edge.visible = isVisible;
  g6Edge.style = { stroke };
};

export const edgeToG6Edge = (
  e: Edge,
  sourceNodeState: NodeState,
  targetNodeState: NodeState,
): G6Edge => {
  const sourceId = nodeIdToG6Id(e.id1);
  const targetId = nodeIdToG6Id(e.id2);

  const g6Edge = {
    id: g6EdgeId(sourceId, targetId),
    source: sourceId,
    target: targetId,
  };

  styleG6Edge(g6Edge, sourceNodeState, targetNodeState);
  return g6Edge;
};

export const collapsedPairwiseEdgeToG6EdgeWithState = (
  e: Edge,
  sourceNodeState: NodeState,
  targetNodeState: NodeState,
): G6Edge => {
  const sourceId = nodeIdToG6Id(e.id1);
  const targetId = collapsedPairwiseNodeIdToG6Id(e.id2.value);

  const g6Edge = {
    id: g6EdgeId(sourceId, targetId),
    source: sourceId,
    target: targetId,
  };

  styleG6Edge(g6Edge, sourceNodeState, targetNodeState);
  return g6Edge;
};

export const transactionEdgeToG6Edge = (
  accountNodeState: NodeState,
  transactionsNodeState: NodeState,
  accountNodeIdAndLightHydration: NodeIdAndLightHydration,
  transactionsNodeIdAndLightHydration: NodeIdAndLightHydration,
): G6DirectedEdgeModelConfig => {
  const isVisible = accountNodeState.visible && transactionsNodeState.visible;
  // make the edge lighter if any connected nodes are dimmed
  let stroke =
    accountNodeState.dimmed ||
    transactionsNodeState.dimmed ||
    accountNodeState.excluded ||
    transactionsNodeState.excluded
      ? DIMMED_COLOUR
      : Color.CoreSlate200;
  if (accountNodeState.new || transactionsNodeState.new) {
    stroke = DesignTokensColor.CoreGreen600;
  }

  if (transactionsNodeIdAndLightHydration.lightHydration.nodeType !== 'transactions') {
    throw new Error('not a transactions edge');
  }

  const transactionsNodeId = transactionsNodeIdAndLightHydration.nodeId;
  const lightlyHydratedTransactionsNode: ServerLightlyHydratedTransactions =
    transactionsNodeIdAndLightHydration.lightHydration.hydrationData;

  const accountNodeId = accountNodeIdAndLightHydration.nodeId;
  let accountTransactionInfo: TransactionAccountInfo;
  switch (accountNodeIdAndLightHydration.lightHydration.nodeType) {
    case 'account':
    case 'external_account':
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      accountTransactionInfo =
        accountNodeIdAndLightHydration.lightHydration.hydrationData['transaction-account-info'];
      break;
    default:
      throw new Error('invalid account node');
  }

  let source: G6NodeId;
  let target: G6NodeId;

  if (isEqual(lightlyHydratedTransactionsNode.account1, accountTransactionInfo)) {
    source = nodeIdToG6Id(accountNodeId);
    target = nodeIdToG6Id(transactionsNodeId);
  } else if (isEqual(lightlyHydratedTransactionsNode.account2, accountTransactionInfo)) {
    source = nodeIdToG6Id(transactionsNodeId);
    target = nodeIdToG6Id(accountNodeId);
  } else {
    throw new Error('inconsistent edge between an account and a transactions node');
  }

  // Reverse the direction of the arrow if the net volume flows in the negative direction.
  if (lightlyHydratedTransactionsNode.netVolume < 0) {
    const tmp = source;
    source = target;
    target = tmp;
  }

  let arrowSizeRadius = 0;
  if (lightlyHydratedTransactionsNode.netVolume !== 0) {
    arrowSizeRadius =
      6 + (3 * Math.log(Math.abs(lightlyHydratedTransactionsNode.netVolume))) / Math.log(10);
  }

  return {
    type: G6_DIRECTED_EDGE,
    id: g6EdgeId(nodeIdToG6Id(accountNodeId), nodeIdToG6Id(transactionsNodeId)),
    source,
    target,
    visible: isVisible,
    style: { stroke },
    arrowSizeRadius,
  };
};

export const createG6RemoveCollapsedGroupMessage = (
  groupId: CollapsedGroupId,
): G6GraphUpdateMessage => {
  return {
    items: [
      {
        messageType: 'remove_collapsed_group',
        id: collapsedGroupIdToG6Id(groupId),
      },
    ],
  };
};

export const createG6RemoveNodeMessage = (nodeId: NodeId): G6GraphUpdateMessage => {
  return {
    items: [
      {
        messageType: 'remove_node',
        id: nodeIdToG6Id(nodeId),
      },
    ],
  };
};
